/**
 * Research-based ROI model constants — v2.1 (literature-aligned).
 *
 * Aligned with the SIFFI ROI Excel v2.1 (R. Hallik 01.2024 + Apr 2026 corrections).
 *
 * Key changes vs v1:
 *   - Direct-effect framing: REDUCTION values are applied directly to baseline
 *     (no multiplication by a separate success-rate factor).
 *   - Richmond et al. 2015 confounder adjustment applied to all gross savings
 *     (×0.77 to remove the share of improvement that happens without EAP).
 *   - Absenteeism baseline raised to 6 days / 90 days (WOS 2021 average across
 *     EAP user population, minus 1.2 days "normal" baseline). The 40 % rate
 *     filter is dropped — already baked into the population average.
 *   - At-risk turnover rate raised to 10 % per Hallik's own annotation
 *     ("at risk thus might be up to 10 %"; her 3.8 % figure was workforce avg).
 *   - Siffi cost computed on TOTAL headcount: 60 €/employee/year. Independent
 *     of platform usage. Used in BOTH base counselling and premium terms.
 *   - Pool basis for savings: actual session users (no troubled-subset construct).
 *
 * Sources (per the original Excel input-sources sheet):
 *   Attridge 2013 (NBC profile) · Riedel 2009 · Stewart 2003 · Boles 2004 ·
 *   Richmond et al. 2015 (J. Occupational Health Psychology) ·
 *   WOS Annual Report 2021 (Attridge / LifeWorks) ·
 *   Nicholson 2006 · Pauly 2008 · SHRM / Agovino 2019 · Wagner 2016.
 */
export const ROI_CONFIG = {
  /** 86 % of EAP users show measurable improvement (Attridge 2013).
   *  Informational only — NOT used in the savings formula in v2.1.
   *  The published REDUCTION values below already incorporate it. */
  EAP_CASE_IMPROVEMENT_RATE: 0.86,

  /** 12.3 % of working capacity lost to presenteeism per 90-day episode
   *  (Riedel 2009 / Stewart 2003 — measured on EAP-user populations). */
  PRESENTEEISM_90D: 0.123,

  /** 23 % overall reduction in presenteeism after EAP intervention.
   *  Applied directly as the average effect across all engaged users. */
  PRESENTEEISM_REDUCTION: 0.23,

  /** 6 absent days per user per 90-day window — WOS 2021 average across the
   *  EAP user population, minus 1.2 days "normal" baseline. No rate filter. */
  ABSENTEEISM_DAYS_90D: 6.0,

  /** 57 % overall reduction in absent days after intervention.
   *  At the optimistic edge of literature (WOS 28-31 %, Vermont 50 %).
   *  Tunable: 0.30 produces a more conservative WOS-aligned figure. */
  ABSENTEEISM_REDUCTION: 0.57,

  /** 10 % annual turnover rate among at-risk users (Hallik annotation
   *  overrides her own 3.8 % constant which was the workforce-average figure). */
  TURNOVER_RATE_AT_RISK: 0.10,

  /** 10 % reduction in turnover post-intervention (Wagner 2016 lower bound). */
  TURNOVER_REDUCTION: 0.10,

  /** Business value generated per EUR of salary (Nicholson 2006, Pauly 2008). */
  PRODUCTIVITY_MULTIPLIER: 1.3,

  /** Replacement cost = 6 × monthly salary (SHRM / Agovino 2019;
   *  midpoint of the cited 4–9 month range). */
  TURNOVER_COST_MULTIPLIER: 6.0,

  /** Standard working days per month */
  WORK_DAYS_PER_MONTH: 20,

  /** Standard working days per year */
  WORKING_DAYS_IN_YEAR: 260,

  /** Richmond et al. 2015: ~23 % of measured improvement happens without EAP
   *  (regression to mean, natural recovery). Applied as ×(1 − 0.23) to all
   *  gross savings. Hallik documented this in the input sheet but didn't
   *  apply it; v2.1 does. */
  RICHMOND_CONFOUNDER: 0.23,

  /* ── Siffi pricing (per TOTAL employee) ─────────────────────────────
   * Annual cost = 12 × (BASE_COUNSELLING_RATE × total × BASE_COUNSELLING_PRICE
   *                   + PREMIUM_RATE × total × PREMIUM_PRICE)
   *             = total × 60 € / year
   */

  /** 30 % of total employees consume base counselling */
  BASE_COUNSELLING_RATE: 0.30,

  /** EUR / month per base counselling user */
  BASE_COUNSELLING_PRICE: 5,

  /** 5 % of total employees use premium */
  PREMIUM_RATE: 0.05,

  /** EUR / month per premium user */
  PREMIUM_PRICE: 70,

  /** Default annual utilization (informational; account-web uses real
   *  usedServicesInPeriodCount from the platform). 20 % is the public-mode
   *  default for the prospect calculator. */
  ANNUAL_UTILIZATION: 0.20,
} as const;

export type RoiConfig = typeof ROI_CONFIG;
